import { z } from 'zod';
import type { CampaignPeriodStatus } from './campaign-status.js';
import type { Currency } from './currency.js';
import { INBOUND_MEDIA_MAX_BYTES } from './inbound-media.js';

/**
 * The most an evidence upload may hold. A photo or a video a fulfilment worker
 * captures on a phone is the same file a donor's WhatsApp message carries, so
 * it is admitted by §5.9's own sniffer under §5.9's own bounds — not by a
 * second set of limits written for this one path.
 */
export const DONATION_EVIDENCE_MAX_BYTES = INBOUND_MEDIA_MAX_BYTES;

export const DONATION_PAYMENT_METHODS = [
  'bank_transfer',
  'cash',
  'card',
  'pos',
  'paypal',
  'direct_debit',
  'other',
  /**
   * The method is not known yet. A clerk taking a donation on the telephone
   * cannot say how the money will arrive, and `other` claims something else —
   * *a method outside this list* — so the two are not interchangeable. It is
   * what staff entry records until a reconciliation match says otherwise, and
   * nothing but a staff entry may write it —
   * docs/decisions/0070-the-server-decides-four-fields-and-a-donation-names-its-parties.md.
   */
  'unspecified',
] as const;
export type DonationPaymentMethod = (typeof DONATION_PAYMENT_METHODS)[number];
export const donationPaymentMethodSchema = z.enum(DONATION_PAYMENT_METHODS);

/**
 * How the row got here. The legacy system answered this question by writing a
 * literal Turkish sentence meaning "donation from the website" into the
 * referrer name field, which is why "how many donations came from the website"
 * was a LIKE query — see docs/decisions/0022-the-donation-record.md.
 */
export const DONATION_ENTRY_CHANNELS = [
  'admin',
  'portal',
  'field',
  /**
   * Historical only. The contact-log module was retired on 2026-09-22
   * (docs/decisions/0066), so nothing writes this channel again — but donations
   * already carry it, so it stays readable, filterable and renderable. It is
   * deliberately absent from `STAFF_ENTRY_CHANNELS` below.
   */
  'contact_log',
  'donation_box',
  'pledge',
  'import',
  /**
   * A donation dictated by staff into a WhatsApp conversation and entered by the
   * n8n intake — docs/decisions/0065. **Deliberately absent from
   * `STAFF_ENTRY_CHANNELS`**: the intake is a signed webhook that knows what it
   * is and sets this itself, exactly as the portal capture sets `portal`, so no
   * client can claim a donation arrived over WhatsApp.
   */
  'whatsapp',
  /**
   * A donation a registered integration client entered over the campaign's own
   * signed endpoint — docs/decisions/0138. Server-set like `whatsapp`: **absent
   * from `STAFF_ENTRY_CHANNELS`**, so no session client can claim it.
   */
  'api',
] as const;
export type DonationEntryChannel = (typeof DONATION_ENTRY_CHANNELS)[number];
export const donationEntryChannelSchema = z.enum(DONATION_ENTRY_CHANNELS);

/**
 * The channels a staff entry screen may claim for itself. Which desk typed a
 * donation is a fact about the desk, not a privilege — but `portal`, `field`,
 * `donation_box`, `pledge` and `import` each mean the row came from somewhere
 * the admin is not, and a client that could name one of those would be
 * rewriting where the money came from. `contact_log` left this list with its
 * module on 2026-09-22 and is now a value staff entry reads but never writes.
 */
export const STAFF_ENTRY_CHANNELS = ['admin'] as const;
export type StaffEntryChannel = (typeof STAFF_ENTRY_CHANNELS)[number];
export const staffEntryChannelSchema = z.enum(STAFF_ENTRY_CHANNELS);

/**
 * The channels whose donations tell nobody: no *KAYIT BİLDİRİMİ*, no
 * `donation.assigned` to the yurt's staff and no `campaign.goal_reached` — and
 * no notice tick that a later act could send. It is a list of channels, never
 * of campaigns, periods or organizations, so "why was nobody told" is answered
 * by the donation's own `entry_channel`. `import` is a staff member entering a
 * season's list from a spreadsheet, and neither it nor any other silent channel
 * is in `STAFF_ENTRY_CHANNELS`, so no client can claim the silence —
 * docs/decisions/0104 §7.
 */
export const SILENT_ENTRY_CHANNELS = ['import'] as const satisfies readonly DonationEntryChannel[];

export function entryChannelAnnounces(channel: DonationEntryChannel): boolean {
  return !(SILENT_ENTRY_CHANNELS as readonly DonationEntryChannel[]).includes(channel);
}

/**
 * §3.3's currency scope: the currencies a member of staff may put on a
 * donation, in the order the entry form offers them. It narrows the staff's
 * choice and nothing else — the organization banks in every one of
 * `CURRENCIES`, so exchange rates, accounts, reconciliation and the portal
 * keep the whole list —
 * docs/decisions/0070-the-server-decides-four-fields-and-a-donation-names-its-parties.md.
 */
export const STAFF_ENTRY_CURRENCIES = ['TRY', 'USD', 'EUR'] as const satisfies readonly Currency[];
export type StaffEntryCurrency = (typeof STAFF_ENTRY_CURRENCIES)[number];

export function isStaffEntryCurrency(currency: Currency): currency is StaffEntryCurrency {
  return (STAFF_ENTRY_CURRENCIES as readonly Currency[]).includes(currency);
}

/**
 * Whether a staff write may leave a donation in `currency`. An entry chooses a
 * currency, so it must be one of `STAFF_ENTRY_CURRENCIES`; a correction chooses
 * one only when it changes it, so keeping the currency already `stored` is
 * never refused — a donation on file in another currency, from before the rule
 * or from a channel that is not the entry form, stays correctable in its own.
 */
export function staffEntryCurrencyAllowed(currency: Currency, stored: Currency | null): boolean {
  return currency === stored || isStaffEntryCurrency(currency);
}

/**
 * "Which month" and "before the start of today" are evaluated in one
 * organizational timezone. A zone-level target spanning four countries across
 * three timezones has to be summed against one boundary or the parts do not
 * add up to the whole. It is a constant rather than an environment variable
 * because it does not vary by environment —
 * docs/decisions/0027-donated-at-and-backdated-entry.md.
 */
export const REPORTING_TIME_ZONE = 'Europe/Istanbul';

/** Enough to absorb a client clock, not enough to hide a date next month. */
export const DONATION_FUTURE_TOLERANCE_MINUTES = 5;

const zoneOffsetMs = (instant: Date, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  const hour = read('hour') % 24;
  const asIfUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    hour,
    read('minute'),
    read('second'),
  );
  return asIfUtc - Math.floor(instant.getTime() / 1000) * 1000;
};

/**
 * Midnight of the calendar day `instant` falls on in `REPORTING_TIME_ZONE`, as
 * a UTC instant. The offset is resolved twice because the offset at the
 * instant is not necessarily the offset at the midnight it implies.
 */
export function startOfReportingDay(instant: Date, timeZone = REPORTING_TIME_ZONE): Date {
  const firstOffset = zoneOffsetMs(instant, timeZone);
  const wallClock = new Date(instant.getTime() + firstOffset);
  const localMidnight = Date.UTC(
    wallClock.getUTCFullYear(),
    wallClock.getUTCMonth(),
    wallClock.getUTCDate(),
  );
  const settledOffset = zoneOffsetMs(new Date(localMidnight - firstOffset), timeZone);
  return new Date(localMidnight - settledOffset);
}

const REPORTING_DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: REPORTING_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * The calendar day `instant` falls on in `REPORTING_TIME_ZONE`, as
 * `YYYY-MM-DD`. A rate day, a monthly target and a reported month are all
 * decided against this one boundary, never the server's offset.
 */
export function reportingDay(instant: Date): string {
  return REPORTING_DAY_FORMAT.format(instant);
}

/**
 * The calendar year `instant` falls in, in `REPORTING_TIME_ZONE`. A receipt is
 * numbered in its issue year on this boundary, so one issued at 01:30 Istanbul
 * time on 1 January belongs to the new year although UTC still reads December —
 * docs/decisions/0046-a-receipt-is-an-acknowledgement-numbered-per-institution.md.
 */
export function reportingYear(instant: Date): number {
  return Number.parseInt(reportingDay(instant).slice(0, 4), 10);
}

export type DonationPeriodWindow = {
  startsAt: Date;
  endsAt: Date;
};

/** Gate 1 — the window is half-open, `[starts_at, ends_at)`. */
export function isWithinPeriodWindow(donatedAt: Date, period: DonationPeriodWindow): boolean {
  return (
    donatedAt.getTime() >= period.startsAt.getTime() &&
    donatedAt.getTime() < period.endsAt.getTime()
  );
}

/** Gate 2 — more than five minutes past the server clock is the future. */
export function isFutureDonationDate(donatedAt: Date, now: Date): boolean {
  return donatedAt.getTime() > now.getTime() + DONATION_FUTURE_TOLERANCE_MINUTES * 60 * 1000;
}

/** Gate 3 — anything before today in `REPORTING_TIME_ZONE` needs a permission. */
export function isBackdatedDonation(donatedAt: Date, now: Date): boolean {
  return donatedAt.getTime() < startOfReportingDay(now).getTime();
}

export type DonationEntryPeriod = DonationPeriodWindow & {
  status: CampaignPeriodStatus;
  acceptsBackdatedUntil: Date | null;
};

export const DONATION_PERIOD_REFUSALS = [
  'CAMPAIGN_PERIOD_NOT_OPEN',
  'CAMPAIGN_PERIOD_CLOSED',
  'DONATION_BACKDATE_NOT_PERMITTED',
] as const;
export type DonationPeriodRefusal = (typeof DONATION_PERIOD_REFUSALS)[number];

/**
 * Whether the period itself takes an entry at this instant, which is a
 * different question from whether `donated_at` is allowed. An `active` period
 * inside its window takes one; a `closed` period takes one until
 * `accepts_backdated_until`, and only from someone holding `donation.backdate`;
 * `draft` and `paused` take none — pausing is how the organization stops taking
 * money for something, so a backlog of paper forms is a reason to resume the
 * period rather than a hole in the gate. See
 * docs/decisions/0027-donated-at-and-backdated-entry.md.
 */
export function checkPeriodAcceptsEntry(input: {
  period: DonationEntryPeriod;
  now: Date;
  mayBackdate: boolean;
}): DonationPeriodRefusal | null {
  const { period, now } = input;

  if (period.status === 'closed') {
    const until = period.acceptsBackdatedUntil;
    if (until === null || now.getTime() > until.getTime()) return 'CAMPAIGN_PERIOD_CLOSED';
    return input.mayBackdate ? null : 'DONATION_BACKDATE_NOT_PERMITTED';
  }

  if (period.status === 'active' && isWithinPeriodWindow(now, period)) return null;
  return 'CAMPAIGN_PERIOD_NOT_OPEN';
}

/**
 * The period gate of an import — docs/decisions/0104 §8. A `closed` season
 * takes an import from someone holding `donation.backdate` **whatever its
 * `accepts_backdated_until` says**, because a closed season is a record of the
 * past and entering last season's list is the point; every row is still dated
 * inside the season's own window by `checkDonationDate`. Every other status is
 * `checkPeriodAcceptsEntry` unchanged, so `paused` and `draft` stay refused and
 * an `active` period outside its window too. Only the `import` channel asks
 * this; a typed entry into a closed season past its deadline is still
 * `CAMPAIGN_PERIOD_CLOSED`.
 */
export function checkPeriodAcceptsImport(input: {
  period: DonationEntryPeriod;
  now: Date;
  mayBackdate: boolean;
}): DonationPeriodRefusal | null {
  if (input.period.status === 'closed') {
    return input.mayBackdate ? null : 'DONATION_BACKDATE_NOT_PERMITTED';
  }
  return checkPeriodAcceptsEntry(input);
}

export const DONATION_DATE_REFUSALS = [
  'DONATION_DATE_OUTSIDE_PERIOD',
  'DONATION_DATE_IN_FUTURE',
  'DONATION_BACKDATE_NOT_PERMITTED',
] as const;
export type DonationDateRefusal = (typeof DONATION_DATE_REFUSALS)[number];

export type DonationDateInput = {
  donatedAt: Date;
  now: Date;
  period: DonationPeriodWindow;
  /** Whether the caller holds `donation.backdate`. */
  mayBackdate: boolean;
};

/**
 * The three gates of docs/decisions/0027-donated-at-and-backdated-entry.md, in
 * the order that record numbers them, as a pure predicate: the first refusal
 * or `null`. It knows nothing about permissions, HTTP status codes or the
 * period's lifecycle — whether a closed period still accepts a late paper form
 * is `accepts_backdated_until`, which the donation service reads beside this.
 */
export function checkDonationDate(input: DonationDateInput): DonationDateRefusal | null {
  if (!isWithinPeriodWindow(input.donatedAt, input.period)) return 'DONATION_DATE_OUTSIDE_PERIOD';
  if (isFutureDonationDate(input.donatedAt, input.now)) return 'DONATION_DATE_IN_FUTURE';
  if (isBackdatedDonation(input.donatedAt, input.now) && !input.mayBackdate) {
    return 'DONATION_BACKDATE_NOT_PERMITTED';
  }
  return null;
}
